/**
 * PHARMAKON — pricing engine · per-unit and per-line margins, comptoir and CHIFA (D-05, A-10, A-13).
 *
 * Owner of the formulas: research/REVENUE_MARGIN.md §4.3-§4.4 (refonte-achats-stock), with the
 * recommended defaults OP-1 (INC = min(TR, PPA) × ρ, TR never uplifted), OP-3 (comptoir revenue = the
 * price actually charged, `montant_ligne_net`) and OP-9 (a pending CHIFA line has no réel margin).
 * Margin basis = the lot's GROSS PU HT; UG units cost 0; ristourne and remise globale are never
 * allocated into it (D-05). Incentive rates come from the pharmacy settings (A-06), never literals.
 *
 * Refonte WP-69 (D-27 amends A-13) — the inverse functions of the A-13 markup, for the types whose
 * « Prix de vente » follows a margin in purchase entry: Prix de vente = PU HT × (1 + marge ÷ 100) and
 * PU HT = Prix de vente ÷ (1 + marge ÷ 100), on the GROSS PU HT and without TVA (A-13 never carries
 * one), each rounded ONCE to the centime, half away from zero. A margin is read in hundredths of a
 * percent (the input's 2-decimal scale, `ECHELLE_POURCENTAGE`).
 */
import {
  appliquerPct,
  diviserArrondi,
  multiplier,
  ratioPct,
  somme,
  versCentiemesPct,
  versCentimes,
  ECHELLE_MARGE_PCT,
} from "./money.js";
import { BUCKET_LOT, CANAL_VENTE } from "./constantes.js";
import { coutSpan } from "./lot-buckets.js";

function estVide(v) {
  return v === null || v === undefined || v === "";
}

function canalValide(canal) {
  if (canal !== CANAL_VENTE.COMPTOIR && canal !== CANAL_VENTE.CHIFA) {
    throw new TypeError(`Canal de vente inconnu : ${String(canal)}`);
  }
  return canal;
}

/**
 * A-10 — CHIFA incentive rate of a product, in hundredths of a percent: the pharmacy's local rate
 * when `Produit.is_local`, else its imported rate (`ParametresPharmacie`, defaults from CONFIG).
 * @param {{ is_local: boolean }} produit
 * @param {{ taux_incentive_chifa_local_pct: *, taux_incentive_chifa_importe_pct: * }} parametres
 */
export function tauxIncentive(produit, parametres) {
  const cle = produit?.is_local ? "taux_incentive_chifa_local_pct" : "taux_incentive_chifa_importe_pct";
  if (!parametres || estVide(parametres[cle])) {
    throw new TypeError(`tauxIncentive : parametres.${cle} est requis (A-06)`);
  }
  return versCentiemesPct(parametres[cle]);
}

/** OP-1 — the incentive base of one unit: the raw CNAS tarif capped at the PPA, like the part CNAS. */
export function baseIncentive(tarifRefCentimes, ppaCentimes) {
  return Math.max(0, Math.min(tarifRefCentimes, ppaCentimes));
}

/**
 * A-13 — marge % = (PPA − PU HT) ÷ PU HT × 100, rounded half away from zero to `echelle` decimals
 * (storage precision by default; WP-69's margin input reads it at 2 decimals); null when PU HT = 0.
 */
export function margePct(ppa, prixAchatHt, echelle = ECHELLE_MARGE_PCT) {
  const pu = versCentimes(prixAchatHt);
  return ratioPct(versCentimes(ppa) - pu, pu, echelle);
}

/** 1 + marge ÷ 100, in hundredths of a percent (30 % → 13 000); a margin of −100 % or less has no inverse. */
function coefficientMarge(marge) {
  if (marge === null || marge === undefined || marge === "") throw new TypeError("Marge requise");
  const c = 10000 + versCentiemesPct(marge);
  if (c <= 0) throw new RangeError(`Marge hors bornes : ${String(marge)} (supérieure à −100 % requise)`);
  return c;
}

/**
 * WP-69 — the Prix de vente of a PU HT at a margin, in centimes: PU HT × (1 + marge ÷ 100), rounded once.
 * prixVenteDepuisMarge("100.00", 30) → 13000 (130,00 DA).
 * @param {*} prixAchatHt  gross PU HT (DA: number | decimal string | Prisma.Decimal)
 * @param {*} marge        margin in % (2 decimals)
 */
export function prixVenteDepuisMarge(prixAchatHt, marge) {
  return diviserArrondi(BigInt(versCentimes(prixAchatHt)) * BigInt(coefficientMarge(marge)), 10000n);
}

/**
 * WP-69 — the PU HT that gives a Prix de vente at a margin, in centimes: Prix de vente ÷ (1 + marge ÷ 100),
 * rounded once. prixAchatDepuisMarge("260.00", 30) → 20000 (200,00 DA). For a margin ≥ 0 the round trip
 * PU HT → Prix de vente → PU HT is exact (the rounding error of the Prix de vente, ≤ ½ centime, shrinks
 * by the coefficient).
 * @param {*} prixVente    Prix de vente / PPA (DA)
 * @param {*} marge        margin in % (2 decimals)
 */
export function prixAchatDepuisMarge(prixVente, marge) {
  return diviserArrondi(BigInt(versCentimes(prixVente)) * 10000n, BigInt(coefficientMarge(marge)));
}

/**
 * D-05 — margin of ONE unit, in centimes.
 *   COMPTOIR: paid PPA − PU HT · UG PPA.
 *   CHIFA (théorique): paid (PPA − PU HT) + TR × ρ · UG PPA + TR × ρ.
 * @param {object} p
 * @param {"COMPTOIR"|"CHIFA"} p.canal
 * @param {"PAYEE"|"UG"} p.bucket
 * @param {*} p.prix_vente_ppa
 * @param {*} p.prix_achat_ht          gross PU HT of the lot
 * @param {*} [p.tarif_ref]            CHIFA only — the product's T.R
 * @param {*} [p.taux_incentive]       CHIFA only — ρ in % (see tauxIncentive / versPct)
 */
export function margeUnitaire({ canal, bucket, prix_vente_ppa, prix_achat_ht, tarif_ref, taux_incentive }) {
  canalValide(canal);
  if (bucket !== BUCKET_LOT.PAYEE && bucket !== BUCKET_LOT.UG) throw new TypeError(`Bucket inconnu : ${String(bucket)}`);
  const ppa = versCentimes(prix_vente_ppa);
  const cout = bucket === BUCKET_LOT.PAYEE ? versCentimes(prix_achat_ht) : 0;
  let incentive = 0;
  if (canal === CANAL_VENTE.CHIFA) {
    if (estVide(taux_incentive)) throw new TypeError("margeUnitaire : taux_incentive est requis pour le canal CHIFA");
    incentive = appliquerPct(baseIncentive(versCentimes(tarif_ref), ppa), versCentiemesPct(taux_incentive));
  }
  return ppa - cout + incentive;
}

/**
 * Revenue and margin of a sale line (REVENUE_MARGIN §4.4), stamped at validation.
 *
 * @param {object} l
 * @param {"COMPTOIR"|"CHIFA"} l.canal
 * @param {Array<{quantite_payee:number, quantite_ug:number, pu_achat_ht_brut:*}>|null} l.spans
 *        consumption spans of the line; null = no lot yet (secours not reconciled) → cost unknown,
 *        the line is excluded from margin figures (REVENUE_MARGIN §8.2)
 * @param {number} [l.quantite]        units sold — required when `spans` is null
 * @param {*} [l.montant_ligne_net]    COMPTOIR: v, the price actually charged (OP-3)
 * @param {*} [l.prix_unitaire_ppa]    CHIFA: PPA of the line
 * @param {*} [l.tarif_remb]           CHIFA: T.R snapshot
 * @param {*} [l.montant_officine]     CHIFA: part CNAS (PS), as computed by the CHIFA split
 * @param {*} [l.montant_assure]       CHIFA: part patient (AS)
 * @param {*} [l.taux_incentive]       CHIFA: ρ snapshot in %
 */
export function calculerLigneVente(l) {
  const canal = canalValide(l?.canal);
  const coutConnu = Array.isArray(l.spans);
  const spans = coutConnu ? l.spans : [];
  const quantitePayee = somme(spans.map((s) => s.quantite_payee ?? 0));
  const quantiteUg = somme(spans.map((s) => s.quantite_ug ?? 0));
  const quantite = coutConnu ? quantitePayee + quantiteUg : Number(l.quantite ?? 0);
  if (!Number.isSafeInteger(quantite) || quantite < 0) throw new RangeError(`Quantité invalide : ${String(l.quantite)}`);
  const cout = coutConnu ? somme(spans.map((s) => coutSpan(s, versCentimes(s.pu_achat_ht_brut)))) : null;

  if (canal === CANAL_VENTE.COMPTOIR) {
    const v = versCentimes(l.montant_ligne_net);
    const marge = cout === null ? null : v - cout;
    return {
      canal,
      quantite,
      quantite_payee: quantitePayee,
      quantite_ug: quantiteUg,
      cout_centimes: cout,
      incentive_theorique_centimes: 0,
      cnas_attendu_centimes: 0,
      montant_assure_centimes: v,
      ca_theorique_centimes: v,
      ca_reel_centimes: v,
      marge_theorique_centimes: marge,
      marge_reelle_centimes: marge,
    };
  }

  if (estVide(l.taux_incentive)) throw new TypeError("calculerLigneVente : taux_incentive est requis pour le canal CHIFA");
  const ppa = versCentimes(l.prix_unitaire_ppa);
  const ps = versCentimes(l.montant_officine);
  const as = versCentimes(l.montant_assure);
  // INC = min(TR, PPA) × n × ρ, rounded once per line (D-05, OP-1).
  const incentive = appliquerPct(multiplier(baseIncentive(versCentimes(l.tarif_remb), ppa), quantite), versCentiemesPct(l.taux_incentive));
  const caTheorique = as + ps + incentive;
  return {
    canal,
    quantite,
    quantite_payee: quantitePayee,
    quantite_ug: quantiteUg,
    cout_centimes: cout,
    incentive_theorique_centimes: incentive,
    cnas_attendu_centimes: ps + incentive,
    montant_assure_centimes: as,
    ca_theorique_centimes: caTheorique,
    ca_reel_centimes: as, // only the patient's share is collected at the counter (D-06)
    marge_theorique_centimes: cout === null ? null : caTheorique - cout,
    marge_reelle_centimes: null, // OP-9: enters réel only when the facture CHIFA is paid
  };
}
